import { escapeHtml } from "@/lib/tool-kit/citation-parts";

/**
 * A structured document, and the three ways it leaves the page.
 *
 * The template generators — lab report, research proposal, case brief, speech outline,
 * SWOT grid, peer-review worksheet — all produce the same kind of thing: a titled
 * document of sections holding paragraphs, lists, labelled fields, tables and, the part
 * that makes it a template rather than a finished text, *prompts* marking what the writer
 * still has to write. Each tool builds a `DocumentModel` with a pure function (so the
 * output is unit-tested), and `DocumentPreview` in `components/tool-kit/document-preview`
 * draws it and offers it as plain text, Markdown, rich HTML for Word and Google Docs,
 * and a print of the document alone.
 *
 * The model rather than markup is the contract because every output needs something
 * different from the same content: Markdown escapes the `*` and `_` in a student's text,
 * HTML escapes `<`, plain text lines a table up with spaces, and an outline's "IV." is
 * native in HTML but has to be computed for the other two. Building every output from
 * one model is what keeps the copy, both downloads and the print saying exactly what the
 * preview says.
 *
 * Every word comes from the caller. The only vocabulary here is `DocumentLabels`, passed
 * in for the reason the kit's primitives take their labels as props: an English default
 * inside a shared module is the one string on the page no translation could reach.
 */

/* ------------------------------------------------------------------ the model */

/** A run of text, optionally emphasised — a book title in a reference, a lead-in. */
export interface DocRun {
  text: string;
  italic?: boolean;
  bold?: boolean;
}

/** Text in the document: a plain string, or runs when part of it needs emphasis. */
export type DocText = string | readonly DocRun[];

export type DocListStyle =
  | "bullet"
  | "decimal"
  | "upper-roman"
  | "upper-alpha"
  | "lower-alpha";

export interface DocListItem {
  text: DocText;
  /** The item is a placeholder the writer still has to replace. Counted as a prompt. */
  prompt?: boolean;
  /** A list under this item — an outline's sub-points. */
  children?: DocList;
}

export interface DocList {
  style: DocListStyle;
  items: readonly DocListItem[];
  /**
   * The first item's ordinal, from 1. An outline that is interrupted by a transition
   * line resumes at "II." rather than restarting at "I.".
   */
  start?: number;
}

export interface DocField {
  label: string;
  /** Missing or blank: a line to fill in by hand. */
  value?: DocText;
}

export interface DocCheckItem {
  text: DocText;
  checked?: boolean;
}

/** One quadrant of a grid — a SWOT or TOWS box. */
export interface DocGridCell {
  title: string;
  /** A qualifier under the title: "Internal · helpful". */
  caption?: string;
  items: readonly DocText[];
  /** Shown as a prompt while `items` is empty. */
  prompt?: DocText;
}

export type DocBlock =
  | { kind: "subheading"; text: DocText }
  | { kind: "paragraph"; text: DocText }
  | { kind: "list"; list: DocList }
  | { kind: "fields"; fields: readonly DocField[] }
  /** A part the writer still has to write. */
  | { kind: "prompt"; text: DocText }
  /** Advice that is not part of the finished text. */
  | { kind: "guidance"; text: DocText }
  | { kind: "checklist"; items: readonly DocCheckItem[] }
  | {
      kind: "table";
      /** Above the table, where science and APA tables put their titles. */
      caption?: DocText;
      head?: readonly DocText[];
      rows: readonly (readonly DocText[])[];
    }
  /** Cells laid out two to a row. */
  | { kind: "grid"; cells: readonly DocGridCell[] }
  /** Blank ruled lines for a handwritten answer. */
  | { kind: "lines"; count: number };

export interface DocSection {
  /** Omitted for a block that sits directly under the title. */
  heading?: DocText;
  blocks: readonly DocBlock[];
}

export interface DocumentModel {
  title: DocText;
  /** A line under the title: a byline, a course, the framework used. */
  subtitle?: DocText;
  sections: readonly DocSection[];
}

/** The document's own two words, supplied by the tool so they can be translated. */
export interface DocumentLabels {
  /** Marks a prompt — a part still to write. "To write". No trailing colon. */
  prompt: string;
  /** Marks guidance that is not part of the finished text. "Tip". No trailing colon. */
  guidance: string;
}

/* ------------------------------------------------------------------ text and markers */

function runsOf(text: DocText): readonly DocRun[] {
  return typeof text === "string" ? [{ text }] : text;
}

const NEWLINES = /\s*\n\s*/g;

/** One line: a stray newline inside a run would break a list item or a table row. */
function oneLine(text: string): string {
  return text.replace(NEWLINES, " ");
}

export function docTextToPlain(text: DocText): string {
  return oneLine(
    runsOf(text)
      .map((run) => run.text)
      .join("")
  );
}

export function isBlankText(text: DocText | undefined): boolean {
  return text === undefined || docTextToPlain(text).trim() === "";
}

const ROMAN: readonly (readonly [number, string])[] = [
  [1000, "M"],
  [900, "CM"],
  [500, "D"],
  [400, "CD"],
  [100, "C"],
  [90, "XC"],
  [50, "L"],
  [40, "XL"],
  [10, "X"],
  [9, "IX"],
  [5, "V"],
  [4, "IV"],
  [1, "I"],
];

export function toRoman(value: number): string {
  let rest = Math.max(1, Math.floor(value));
  let out = "";
  for (const [size, glyph] of ROMAN) {
    while (rest >= size) {
      out += glyph;
      rest -= size;
    }
  }
  return out;
}

/** 1 → A, 26 → Z, 27 → AA: the same sequence CSS `upper-alpha` counts in. */
export function toLetters(value: number): string {
  let rest = Math.max(1, Math.floor(value));
  let out = "";
  while (rest > 0) {
    rest -= 1;
    out = String.fromCharCode(65 + (rest % 26)) + out;
    rest = Math.floor(rest / 26);
  }
  return out;
}

/** "•", "3.", "IV.", "C.", "c." — the marker a text or Markdown export writes. */
export function listMarker(style: DocListStyle, ordinal: number): string {
  switch (style) {
    case "decimal":
      return `${ordinal}.`;
    case "upper-roman":
      return `${toRoman(ordinal)}.`;
    case "upper-alpha":
      return `${toLetters(ordinal)}.`;
    case "lower-alpha":
      return `${toLetters(ordinal).toLowerCase()}.`;
    default:
      return "•";
  }
}

/** How many prompts are still in the document — the "parts still to write" count. */
export function countPrompts(doc: DocumentModel): number {
  let total = 0;
  const countList = (list: DocList) => {
    for (const item of list.items) {
      if (item.prompt) {
        total += 1;
      }
      if (item.children) {
        countList(item.children);
      }
    }
  };
  for (const section of doc.sections) {
    for (const block of section.blocks) {
      if (block.kind === "prompt") {
        total += 1;
      } else if (block.kind === "list") {
        countList(block.list);
      } else if (block.kind === "grid") {
        total += block.cells.filter(
          (cell) => cell.items.length === 0 && cell.prompt !== undefined
        ).length;
      }
    }
  }
  return total;
}

/* ------------------------------------------------------------------ plain text */

/** A blank to write on, in text and Markdown. */
const BLANK = "________________________";
const WRITING_LINE =
  "____________________________________________________________";
/** Wider than this, a text table is written as one "Heading: value" record per row. */
const TEXT_TABLE_WIDTH = 100;

function underline(char: string, text: string): string {
  return char.repeat(Math.min(Math.max(text.length, 3), 72));
}

function plainPrompt(text: DocText, labels: DocumentLabels): string {
  return `[${labels.prompt}: ${docTextToPlain(text)}]`;
}

function listToText(
  list: DocList,
  labels: DocumentLabels,
  indent = ""
): string[] {
  const lines: string[] = [];
  const start = list.start ?? 1;
  for (const [index, item] of list.items.entries()) {
    const marker = listMarker(list.style, start + index);
    const body = item.prompt
      ? plainPrompt(item.text, labels)
      : docTextToPlain(item.text);
    lines.push(`${indent}${marker} ${body}`);
    if (item.children) {
      lines.push(...listToText(item.children, labels, `${indent}    `));
    }
  }
  return lines;
}

function alignedTable(cells: string[][], hasHead: boolean): string[] | null {
  const columns = Math.max(...cells.map((row) => row.length));
  const widths = Array.from({ length: columns }, (_, column) =>
    Math.max(3, ...cells.map((row) => (row[column] ?? "").length))
  );
  const total = widths.reduce((sum, width) => sum + width, 0);
  if (total + 3 * (columns - 1) > TEXT_TABLE_WIDTH) {
    return null;
  }
  const line = (row: string[]) =>
    widths
      .map((width, column) => (row[column] ?? "").padEnd(width))
      .join(" | ")
      .trimEnd();
  const lines = cells.map(line);
  if (hasHead) {
    lines.splice(1, 0, widths.map((width) => "-".repeat(width)).join("-+-"));
  }
  return lines;
}

/** Too wide to line up: each row becomes a short record of "Heading: value" lines. */
function stackedTable(cells: string[][], head: string[] | null): string[] {
  const records = (head ? cells.slice(1) : cells).map((row) =>
    row
      .map((cell, column) => {
        const label = head?.[column];
        return label ? `${label}: ${cell}` : cell;
      })
      .join("\n")
  );
  return [records.join("\n\n")];
}

function tableToText(block: Extract<DocBlock, { kind: "table" }>): string {
  const head = block.head ? block.head.map(docTextToPlain) : null;
  const body = block.rows.map((row) => row.map(docTextToPlain));
  const cells = head ? [head, ...body] : body;
  const lines = alignedTable(cells, head !== null) ?? stackedTable(cells, head);
  const caption = block.caption ? [docTextToPlain(block.caption)] : [];
  return [...caption, ...lines].join("\n");
}

function gridToText(
  block: Extract<DocBlock, { kind: "grid" }>,
  labels: DocumentLabels
): string {
  return block.cells
    .map((cell) => {
      const title = cell.caption
        ? `${cell.title} (${cell.caption})`
        : cell.title;
      const body =
        cell.items.length > 0
          ? cell.items.map((item) => `• ${docTextToPlain(item)}`)
          : [plainPrompt(cell.prompt ?? "", labels)];
      return [title, ...body].join("\n");
    })
    .join("\n\n");
}

function blockToText(block: DocBlock, labels: DocumentLabels): string {
  switch (block.kind) {
    case "subheading":
    case "paragraph":
      return docTextToPlain(block.text);
    case "list":
      return listToText(block.list, labels).join("\n");
    case "fields":
      return block.fields
        .map((field) =>
          isBlankText(field.value)
            ? `${field.label}: ${BLANK}`
            : `${field.label}: ${docTextToPlain(field.value ?? "")}`
        )
        .join("\n");
    case "prompt":
      return plainPrompt(block.text, labels);
    case "guidance":
      return `${labels.guidance}: ${docTextToPlain(block.text)}`;
    case "checklist":
      return block.items
        .map(
          (item) =>
            `${item.checked ? "[x]" : "[ ]"} ${docTextToPlain(item.text)}`
        )
        .join("\n");
    case "table":
      return tableToText(block);
    case "grid":
      return gridToText(block, labels);
    default:
      return Array.from({ length: block.count }, () => WRITING_LINE).join(
        "\n\n"
      );
  }
}

/** The document as plain text: underlined headings, real list markers, aligned tables. */
export function documentToText(
  doc: DocumentModel,
  labels: DocumentLabels
): string {
  const title = docTextToPlain(doc.title);
  const chunks = [`${title}\n${underline("=", title)}`];
  if (doc.subtitle) {
    chunks.push(docTextToPlain(doc.subtitle));
  }
  for (const section of doc.sections) {
    if (section.heading) {
      const heading = docTextToPlain(section.heading);
      chunks.push(`${heading}\n${underline("-", heading)}`);
    }
    for (const block of section.blocks) {
      chunks.push(blockToText(block, labels));
    }
  }
  return `${chunks.filter((chunk) => chunk !== "").join("\n\n")}\n`;
}

/* ------------------------------------------------------------------ Markdown */

const MARKDOWN_SPECIAL = /[\\`*_[\]<]/g;
/** "# ", "> ", "- " and "+ " open structure at the start of a line… */
const LINE_START_SYMBOL = /^([#>]|[-+](?=\s))/;
/** …and so does "1. " — escaped at the period, because "\1" is not an escape. */
const LINE_START_NUMBER = /^(\d+)([.)])(?=\s)/;
const TABLE_PIPE = /\|/g;
const EDGE_SPACE = /^(\s*)([\s\S]*?)(\s*)$/;

function escapeMarkdown(text: string): string {
  return oneLine(text).replace(MARKDOWN_SPECIAL, "\\$&");
}

function runToMarkdown(run: DocRun): string {
  const escaped = escapeMarkdown(run.text);
  if (!(run.italic || run.bold) || escaped.trim() === "") {
    return escaped;
  }
  // Emphasis cannot open or close on a space, so leading and trailing spaces move out.
  const [, lead = "", core = "", trail = ""] = EDGE_SPACE.exec(escaped) ?? [];
  const mark = run.bold ? "**" : "*";
  const inner = run.bold && run.italic ? `*${core}*` : core;
  return `${lead}${mark}${inner}${mark}${trail}`;
}

function md(text: DocText): string {
  return runsOf(text).map(runToMarkdown).join("");
}

/** A paragraph that happens to open with "# " or "1. " must not turn into structure. */
function mdParagraph(text: DocText): string {
  return md(text)
    .replace(LINE_START_SYMBOL, "\\$1")
    .replace(LINE_START_NUMBER, "$1\\$2");
}

function markdownPrompt(text: DocText, labels: DocumentLabels): string {
  return `[${escapeMarkdown(labels.prompt)}: ${md(text)}]`;
}

function listToMarkdown(
  list: DocList,
  labels: DocumentLabels,
  indent = ""
): string[] {
  const lines: string[] = [];
  const start = list.start ?? 1;
  for (const [index, item] of list.items.entries()) {
    const ordinal = start + index;
    // Markdown counts in decimals only; an outline's "IV." is written into a bullet.
    let marker = "-";
    if (list.style === "decimal") {
      marker = `${ordinal}.`;
    } else if (list.style !== "bullet") {
      marker = `- ${listMarker(list.style, ordinal)}`;
    }
    const body = item.prompt
      ? markdownPrompt(item.text, labels)
      : md(item.text);
    lines.push(`${indent}${marker} ${body}`);
    if (item.children) {
      lines.push(...listToMarkdown(item.children, labels, `${indent}    `));
    }
  }
  return lines;
}

function markdownCell(text: DocText): string {
  const cell = md(text).replace(TABLE_PIPE, "\\|").trim();
  return cell === "" ? " " : cell;
}

function tableToMarkdown(block: Extract<DocBlock, { kind: "table" }>): string {
  const width = Math.max(
    block.head?.length ?? 0,
    ...block.rows.map((row) => row.length)
  );
  const pad = (row: readonly DocText[]) =>
    Array.from({ length: width }, (_, column) =>
      markdownCell(row[column] ?? "")
    );
  const line = (cells: string[]) => `| ${cells.join(" | ")} |`;
  // A pipe table needs a header row; a headless table gets an empty one.
  const head = block.head ? pad(block.head) : pad([]);
  const lines = [
    line(head),
    line(head.map(() => "---")),
    ...block.rows.map((row) => line(pad(row))),
  ];
  const caption = block.caption ? [mdParagraph(block.caption), ""] : [];
  return [...caption, ...lines].join("\n");
}

function gridToMarkdown(
  block: Extract<DocBlock, { kind: "grid" }>,
  labels: DocumentLabels
): string {
  return block.cells
    .map((cell) => {
      const head = [`### ${escapeMarkdown(cell.title)}`];
      if (cell.caption) {
        head.push("", `*${escapeMarkdown(cell.caption)}*`);
      }
      const body =
        cell.items.length > 0
          ? cell.items.map((item) => `- ${md(item)}`).join("\n")
          : `> **${escapeMarkdown(labels.prompt)}:** ${md(cell.prompt ?? "")}`;
      return [...head, "", body].join("\n");
    })
    .join("\n\n");
}

function blockToMarkdown(block: DocBlock, labels: DocumentLabels): string {
  switch (block.kind) {
    case "subheading":
      return `### ${md(block.text)}`;
    case "paragraph":
      return mdParagraph(block.text);
    case "list":
      return listToMarkdown(block.list, labels).join("\n");
    case "fields":
      return block.fields
        .map((field) => {
          const value = isBlankText(field.value)
            ? BLANK
            : md(field.value ?? "");
          return `- **${escapeMarkdown(field.label)}:** ${value}`;
        })
        .join("\n");
    case "prompt":
      return `> **${escapeMarkdown(labels.prompt)}:** ${md(block.text)}`;
    case "guidance":
      return `*${escapeMarkdown(labels.guidance)}:* ${md(block.text)}`;
    case "checklist":
      return block.items
        .map((item) => `- ${item.checked ? "[x]" : "[ ]"} ${md(item.text)}`)
        .join("\n");
    case "table":
      return tableToMarkdown(block);
    case "grid":
      return gridToMarkdown(block, labels);
    default:
      // A line of underscores alone is a thematic break: ruled lines, raw or rendered.
      return Array.from({ length: block.count }, () => WRITING_LINE).join(
        "\n\n"
      );
  }
}

/** The document as Markdown (CommonMark with GitHub task lists and pipe tables). */
export function documentToMarkdown(
  doc: DocumentModel,
  labels: DocumentLabels
): string {
  const chunks = [`# ${md(doc.title)}`];
  if (doc.subtitle) {
    chunks.push(mdParagraph(doc.subtitle));
  }
  for (const section of doc.sections) {
    if (section.heading) {
      chunks.push(`## ${md(section.heading)}`);
    }
    for (const block of section.blocks) {
      chunks.push(blockToMarkdown(block, labels));
    }
  }
  return `${chunks.filter((chunk) => chunk !== "").join("\n\n")}\n`;
}

/* ------------------------------------------------------------------ HTML */

/*
 * Inline styles rather than classes: this HTML is pasted into Word and Google Docs,
 * which keep an element's own style attribute and know nothing of the page's CSS.
 */
const CELL_STYLE =
  "border:1px solid #888;padding:6px 8px;vertical-align:top;text-align:left;";
const TABLE_STYLE = "border-collapse:collapse;width:100%;margin:6px 0 12px;";
const PROMPT_STYLE = "border:1px dashed #888;padding:4px 8px;";
const MUTED_STYLE = "color:#555;";
const HTML_LIST_TYPE: Record<DocListStyle, string> = {
  bullet: "",
  decimal: "1",
  "upper-roman": "I",
  "upper-alpha": "A",
  "lower-alpha": "a",
};

function runToHtml(run: DocRun): string {
  let out = escapeHtml(oneLine(run.text));
  if (run.italic) {
    out = `<em>${out}</em>`;
  }
  if (run.bold) {
    out = `<strong>${out}</strong>`;
  }
  return out;
}

function html(text: DocText): string {
  return runsOf(text).map(runToHtml).join("");
}

function htmlPrompt(text: DocText, labels: DocumentLabels): string {
  return `<em style="${MUTED_STYLE}">[${escapeHtml(labels.prompt)}: ${html(text)}]</em>`;
}

function listToHtml(list: DocList, labels: DocumentLabels): string {
  const items = list.items
    .map((item) => {
      const body = item.prompt
        ? htmlPrompt(item.text, labels)
        : html(item.text);
      const children = item.children ? listToHtml(item.children, labels) : "";
      return `<li>${body}${children}</li>`;
    })
    .join("");
  if (list.style === "bullet") {
    return `<ul>${items}</ul>`;
  }
  const start = list.start && list.start > 1 ? ` start="${list.start}"` : "";
  return `<ol type="${HTML_LIST_TYPE[list.style]}"${start}>${items}</ol>`;
}

function cellHtml(text: DocText, tag: "td" | "th"): string {
  const body = html(text);
  return `<${tag} style="${CELL_STYLE}">${body === "" ? "&nbsp;" : body}</${tag}>`;
}

function tableToHtml(block: Extract<DocBlock, { kind: "table" }>): string {
  const caption = block.caption
    ? `<caption style="text-align:left;font-weight:bold;padding-bottom:4px;">${html(block.caption)}</caption>`
    : "";
  const head = block.head
    ? `<thead><tr>${block.head.map((cell) => cellHtml(cell, "th")).join("")}</tr></thead>`
    : "";
  const rows = block.rows
    .map(
      (row) => `<tr>${row.map((cell) => cellHtml(cell, "td")).join("")}</tr>`
    )
    .join("");
  return `<table style="${TABLE_STYLE}">${caption}${head}<tbody>${rows}</tbody></table>`;
}

function gridCellHtml(cell: DocGridCell, labels: DocumentLabels): string {
  const caption = cell.caption
    ? `<p style="margin:0 0 4px;${MUTED_STYLE}"><em>${escapeHtml(cell.caption)}</em></p>`
    : "";
  const body =
    cell.items.length > 0
      ? `<ul>${cell.items.map((item) => `<li>${html(item)}</li>`).join("")}</ul>`
      : `<p>${htmlPrompt(cell.prompt ?? "", labels)}</p>`;
  return `<td style="${CELL_STYLE}width:50%;"><p style="margin:0;"><strong>${escapeHtml(cell.title)}</strong></p>${caption}${body}</td>`;
}

function gridToHtml(
  block: Extract<DocBlock, { kind: "grid" }>,
  labels: DocumentLabels
): string {
  const rows: string[] = [];
  for (let index = 0; index < block.cells.length; index += 2) {
    const pair = block.cells.slice(index, index + 2);
    rows.push(
      `<tr>${pair.map((cell) => gridCellHtml(cell, labels)).join("")}</tr>`
    );
  }
  return `<table style="${TABLE_STYLE}"><tbody>${rows.join("")}</tbody></table>`;
}

function blockToHtml(block: DocBlock, labels: DocumentLabels): string {
  switch (block.kind) {
    case "subheading":
      return `<h3>${html(block.text)}</h3>`;
    case "paragraph":
      return `<p>${html(block.text)}</p>`;
    case "list":
      return listToHtml(block.list, labels);
    case "fields":
      return block.fields
        .map((field) => {
          const value = isBlankText(field.value)
            ? BLANK
            : html(field.value ?? "");
          return `<p><strong>${escapeHtml(field.label)}:</strong> ${value}</p>`;
        })
        .join("");
    case "prompt":
      return `<p style="${PROMPT_STYLE}"><strong>${escapeHtml(labels.prompt)}:</strong> ${html(block.text)}</p>`;
    case "guidance":
      return `<p style="${MUTED_STYLE}"><em>${escapeHtml(labels.guidance)}:</em> ${html(block.text)}</p>`;
    case "checklist":
      return `<ul style="list-style:none;padding-left:0;">${block.items
        .map(
          (item) => `<li>${item.checked ? "☑" : "☐"} ${html(item.text)}</li>`
        )
        .join("")}</ul>`;
    case "table":
      return tableToHtml(block);
    case "grid":
      return gridToHtml(block, labels);
    default:
      return Array.from(
        { length: block.count },
        () => `<p>${WRITING_LINE}</p>`
      ).join("");
  }
}

/** An HTML fragment for the clipboard: Word and Google Docs paste it with its formatting. */
export function documentToHtml(
  doc: DocumentModel,
  labels: DocumentLabels
): string {
  const parts = [`<h1>${html(doc.title)}</h1>`];
  if (doc.subtitle) {
    parts.push(`<p>${html(doc.subtitle)}</p>`);
  }
  for (const section of doc.sections) {
    if (section.heading) {
      parts.push(`<h2>${html(section.heading)}</h2>`);
    }
    for (const block of section.blocks) {
      parts.push(blockToHtml(block, labels));
    }
  }
  return parts.join("\n");
}

const PRINT_CSS = [
  "@page{margin:2cm}",
  'body{margin:0;color:#000;font-family:Georgia,"Times New Roman",serif;font-size:12pt;line-height:1.45}',
  "h1{font-size:18pt;margin:0 0 6pt}",
  "h2{font-size:14pt;margin:18pt 0 6pt;break-after:avoid}",
  "h3{font-size:12pt;margin:12pt 0 4pt;break-after:avoid}",
  "p,li{margin:0 0 6pt}",
  "tr,li,p{break-inside:avoid}",
].join("");

/** A complete page holding only the document, for `printHtmlDocument`. */
export function documentToPrintHtml(
  doc: DocumentModel,
  labels: DocumentLabels,
  lang: string
): string {
  return [
    "<!DOCTYPE html>",
    `<html lang="${escapeHtml(lang)}"><head><meta charset="utf-8">`,
    `<title>${escapeHtml(docTextToPlain(doc.title))}</title>`,
    `<style>${PRINT_CSS}</style></head><body>`,
    documentToHtml(doc, labels),
    "</body></html>",
  ].join("\n");
}
